
import labelsAndHelpText from "../../constants/text";

const COMMON_FIELD_MAPPINGS = {
  isCTSUCompletedTesting: labelsAndHelpText.openRave.ctsuTestingForm?.isCTSUCompletedTesting?.label,
  isGroupCompletedTesting: labelsAndHelpText.openRave.groupTestingForm?.isGroupCompletedTesting?.label,
  isOpenAdminSetupCompleted: labelsAndHelpText.openRave.uatSetupForm?.isOpenAdminSetupCompleted?.label,
  formIdsList: labelsAndHelpText.openRave.uatSetupForm?.formIdsList?.label,
  testSitesList: labelsAndHelpText.openRave.uatSetupForm?.testSitesList?.label,
  primaryOpenAdmin: labelsAndHelpText.openRave.openRaveSubmitForm?.primaryOpenAdmin?.label,
  secondaryOpenAdmin: labelsAndHelpText.openRave.openRaveSubmitForm?.secondaryOpenAdmin?.label,
  primaryComments: labelsAndHelpText.openRave.openRaveSubmitForm?.primaryComments?.label,
  secondaryComments: labelsAndHelpText.openRave.openRaveSubmitForm?.secondaryComments?.label,
  setupCompletedProd: labelsAndHelpText.openRave.completedForm?.setupCompletedProd?.label,
  formsActivatedProd: labelsAndHelpText.openRave.completedForm?.formsActivatedProd?.label,
  releaseDateTime: labelsAndHelpText.openRave.pendingSignOffForm?.releaseDateTime?.label,
  groupAdminSignOff: labelsAndHelpText.openRave.pendingSignOffForm?.groupAdminSignOff?.label,
  groupAdminComments: labelsAndHelpText.openRave.pendingSignOffForm?.groupAdminComments?.label,
  openAdminSignOff: labelsAndHelpText.openRave.pendingSignOffForm?.openAdminSignOff?.label,
  openAdminComments: labelsAndHelpText.openRave.pendingSignOffForm?.openAdminComments?.label,
  irocAdminSignOff: labelsAndHelpText.openRave.pendingSignOffForm?.irocAdminSignOff?.label,
  irocAdminComments: labelsAndHelpText.openRave.pendingSignOffForm?.irocAdminComments?.label,
  files: labelsAndHelpText.openRave.ctsuTestingForm?.files?.label,
  comments: labelsAndHelpText.openRave.ctsuTestingForm?.comments?.label
};


export const commaSeparateToSnakeCase = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.split(",").map(s => s?.trim()).join("_").toLowerCase();
};

export const updateLovValuesToSnakeCase = (lov) => {
  if (!Array.isArray(lov)) return [];
  return lov.map((item) => ({
    ...item,
    value: commaSeparateToSnakeCase(item.value)
  }));
}

export const FORM_HISTORY_COLUMNS = [
  {
    label: "Tab Updated",
    name: "tabUpdated",
    filter: true,
    sortable: true
  },
  {
    label: "Fields Updated",
    name: "fieldsUpdated",
    filter: true,
    sortable: true,
    showWhitespace: true
  },
  {
    label: "Updated Date",
    name: "updatedDate",
    type: "date",
    filter: true,
    sortable: true

  },
  {
    label: "Updated By",
    name: "updatedByFormatted",
    filter: true,
    sortable: true
  },
  {
    label: "Updated By",
    name: "updatedBy",
    filter: true,
    sortable: true,
    visible : false
  }
];

/**
 * Helper function to format field names nicely
 * @param {string} fieldName - The field name to format
 * @returns {string} - Formatted field name
 */
export const formatFieldName = (fieldName) => {

  const cleanName = fieldName.replace(/\.\d+\./g, ".").replace(/\.\d+$/, "");
  const finalName = cleanName.includes(".") ? cleanName.split(".").pop() : cleanName;


  const mappedLabel = COMMON_FIELD_MAPPINGS[finalName];
  if (mappedLabel) {
    return mappedLabel;
  }


  return finalName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

/**
 * Function to map field names to their display labels
 * @param {string} fieldName - The field name to map
 * @param {string} formType - The form type (default: "openRave")
 * @returns {string} - The display label for the field
 */
export const getFieldLabel = (fieldName, formType = "openRave") => {

  const baseFieldName = fieldName.includes(".") ? fieldName.split(".")[0] : fieldName;
  const nestedFieldName = fieldName.includes(".") ? fieldName.split(".").pop() : null;

  const findLabelInFormLabels = (formLabels) => {
    if (!formLabels) return null;

    const formSections = Object.values(formLabels);
    return formSections.reduce((label, formSection) => {
      if (label) return label;
      if (!formSection || typeof formSection !== "object") return null;

      if (formSection[fieldName]?.label) {
        return formSection[fieldName].label;
      }

      if (formSection[baseFieldName]?.label) {
        return formSection[baseFieldName].label;
      }

      if (formSection.fields && formSection.fields[fieldName]?.label) {
        return formSection.fields[fieldName].label;
      }

      if (
        nestedFieldName &&
        formSection[baseFieldName]?.fields &&
        formSection[baseFieldName].fields[nestedFieldName]?.label
      ) {
        return formSection[baseFieldName].fields[nestedFieldName].label;
      }

      if (nestedFieldName && formSection.fields && formSection.fields[nestedFieldName]?.label) {
        return formSection.fields[nestedFieldName].label;
      }

      return null;
    }, null);
  };

  const formLabels = labelsAndHelpText[formType];
  const foundLabelInFormType = findLabelInFormLabels(formLabels);
  if (foundLabelInFormType) {
    return foundLabelInFormType;
  }

  const foundLabelInOtherFormType = Object.entries(labelsAndHelpText)
    .filter(([type]) => type !== formType)
    .map(([, labels]) => findLabelInFormLabels(labels))
    .find((label) => !!label);

  if (foundLabelInOtherFormType) {
    return foundLabelInOtherFormType;
  }


  return formatFieldName(fieldName);
};

/**
 * Function to convert field names to labels
 * @param {string[]} fieldsArray - Array of field names
 * @returns {string[]} - Array of field labels
 */
export const mapFieldsToLabels = (fields = "") => {
  const fieldsArray = fields?.trim()?.split(",");
  if (!fieldsArray || !Array.isArray(fieldsArray)) return [];
  return fieldsArray.map((fieldName) => getFieldLabel(fieldName?.trim()));
};

/**
 * Function to format history data for the grid
 * @param {Array} data - Raw history data
 * @returns {Array} - Formatted data for the grid
 */
export const formatHistoryData = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item, index) => {

    const fieldLabels = item.updatedFields ? mapFieldsToLabels(item.updatedFields) : [];
    const updatedDate = item.updatedDate || item.changedDate || "";
    const id = updatedDate ? new Date(updatedDate).getTime() + index : index + 1;
    return {
      id,
      tabUpdated: item.tabName || "N/A",
      fieldsUpdated: fieldLabels.length > 0 ? fieldLabels.join(", ") : "N/A",
      updatedDate,
      updatedBy: item.updatedBy || item.changedBy || "",
      updatedByFormatted: commaSeparateToSnakeCase(item.updatedBy || item.changedBy || "")
    };
  });
};

/**
 * Generate dynamic LOV options from data for a specific field
 * @param {Array} data - The data array to extract values from
 * @param {string|Array<string>} fieldNames - Field name(s) to extract (can be a single field or array of fallback fields)
 * @param {Array<string>} excludeValues - Values to exclude from the options (default: ["N/A"])
 * @returns {Array} Array of {label, value} objects for the LOV
 */
export const generateDynamicOptions = (data, fieldNames, excludeValues = ["N/A"]) => {
  if (!data || !Array.isArray(data)) return [];

  const fields = Array.isArray(fieldNames) ? fieldNames : [fieldNames];
  const uniqueValues = new Set();

  data.forEach((item) => {

    const value = fields.find((field) => item[field]) ? item[fields.find((field) => item[field])] : null;
    if (value && !excludeValues.includes(value)) {
      uniqueValues.add(value);
    }
  });

  return Array.from(uniqueValues)
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: val,
      value: val
    }));
};

